<script setup lang="ts">
import { computed, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import { useAuthStore } from '@creditify/shared-fe/stores/auth'
import { useWorkspaceStore } from '@creditify/shared-fe/stores/workspace'
import AppFooter from './components/AppFooter.vue'

const route = useRoute()
const showFooter = computed(() => {
  const p = route.path
  return p !== '/login' && p !== '/register' && !p.startsWith('/legal/')
})

// Load the workspace store app-wide as soon as the user is authenticated, so
// EVERY page (not just the dashboard) has `workspaces.active` populated and
// scopes its API calls correctly. Without this, deep-linking straight to
// /budget etc. renders "no workspace" and unscoped calls. Runs on initial
// mount (initialize() may have already restored a session) and whenever the
// auth state flips to authenticated (login/register).
const auth = useAuthStore()
const workspaces = useWorkspaceStore()
const isAuthenticated = computed(() => auth.isAuthenticated)

function ensureWorkspaces() {
  if (isAuthenticated.value && !workspaces.initialized && !workspaces.loading) {
    workspaces.load().catch(() => {
      /* best-effort; individual pages surface their own load errors */
    })
  }
  if (!isAuthenticated.value && workspaces.initialized) {
    workspaces.clear()
  }
}

watch(isAuthenticated, ensureWorkspaces, { immediate: true })
</script>

<template>
  <RouterView />
  <AppFooter v-if="showFooter" />
</template>

<style>
/*
 * Legacy --color-* aliases now defer to the Flexoki semantic tokens
 * (defined in shared-fe/styles/flexoki.css). Existing scoped styles that
 * reference var(--color-*) keep working AND gain dark-mode support for free.
 */
:root {
  --color-primary: var(--accent);
  --color-fg: var(--fg);
  --color-bg: var(--bg);
  --color-card: var(--bg-card);
  --color-border: var(--border);
  --color-muted: var(--fg-muted);
}

* { box-sizing: border-box; }
body {
  margin: 0;
  font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
  background: var(--color-bg);
  color: var(--color-fg);
}

button {
  font: inherit;
  cursor: pointer;
}
</style>
